import React, { useState, useMemo } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Input,
    Select,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    useColorMode,
    useColorModeValue,
    Card,
    CardBody,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    InputGroup,
    InputLeftElement,
    Stat,
    StatLabel,
    StatNumber,
    SimpleGrid,
    Wrap,
    WrapItem,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Tooltip,
    Icon,
} from "@chakra-ui/react";
import { SearchIcon, WarningIcon, CheckCircleIcon, InfoIcon, LockIcon, ViewIcon } from "@chakra-ui/icons";
import {
    FiShield,
    FiBox,
    FiAlertTriangle,
    FiGrid,
    FiList,
    FiUmbrella,
    FiActivity,
    FiKey,
    FiUsers,
    FiEye,
    FiEdit,
    FiTrash2,
    FiPlusCircle,
    FiDollarSign,
    FiStar,
    FiTarget,
    FiLayers,
    FiCheckCircle,
    FiMap,
} from "react-icons/fi";
import { getColor } from "../brandConfig";
import { usePageTitle } from "../hooks/useDocumentTitle";
import { useQuery, gql } from "@apollo/client";

// ==========================================
// GraphQL Query
// ==========================================

const PERMISSIONS_AUDIT = gql`
    query PermissionsAudit {
        permissionsAudit {
            totalPermissions
            totalModules
            orphanCount
            permissions {
                name
                description
                category
                moduleCount
                isOrphan
                usedInModules {
                    moduleId
                    levels
                }
            }
            modules {
                moduleId
                levels
                permissionsUsed
            }
        }
    }
`;

// ==========================================
// Types
// ==========================================

interface PermissionModuleUsage {
    moduleId: string;
    levels: string[];
}

interface PermissionAuditEntry {
    name: string;
    description: string;
    category: string;
    moduleCount: number;
    isOrphan: boolean;
    usedInModules: PermissionModuleUsage[];
}

interface ModuleAuditEntry {
    moduleId: string;
    levels: string[];
    permissionsUsed: string[];
}

interface PermissionsAuditData {
    permissionsAudit: {
        totalPermissions: number;
        totalModules: number;
        orphanCount: number;
        permissions: PermissionAuditEntry[];
        modules: ModuleAuditEntry[];
    };
}

// ==========================================
// Helpers
// ==========================================

const CATEGORY_COLORS: Record<string, string> = {
    system: "purple",
    umbrella: "teal",
    module: "blue",
    "field-level": "yellow",
    role: "gray",
};

const LEVEL_COLORS: Record<string, string> = {
    view: "green",
    create: "blue",
    edit: "orange",
    delete: "red",
    financial: "purple",
    s8: "red",
    risk: "orange",
    reports: "cyan",
    config: "gray",
    send: "teal",
    admin: "red",
    addressBook: "blue",
    share: "teal",
    permissions: "purple",
    merge: "orange",
    publish: "green",
    review: "cyan",
    governance: "purple",
};

function getCategoryLabel(category: string): string {
    const labels: Record<string, string> = {
        system: "System",
        umbrella: "Umbrella Role",
        module: "Module",
        "field-level": "Field-Level",
        role: "Business Role",
    };
    return labels[category] || category;
}

const CATEGORY_ICONS: Record<string, any> = {
    system: FiShield,
    umbrella: FiUmbrella,
    module: FiBox,
    "field-level": FiEye,
    role: FiUsers,
};

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
    system: "Core system permissions that control platform-level access. Checked at middleware level.",
    umbrella: "Cross-cutting roles that grant access to many modules at once. Use sparingly.",
    module: "Scoped to a single module. The standard pattern for new permissions.",
    "field-level": "Controls visibility of specific fields within a module, not whole-module access.",
    role: "Business identity flags (seller, client, employee). Used for routing, not module access.",
};

const LEVEL_ICONS: Record<string, any> = {
    view: FiEye,
    create: FiPlusCircle,
    edit: FiEdit,
    delete: FiTrash2,
    financial: FiDollarSign,
    s8: LockIcon,
    admin: FiShield,
    review: FiCheckCircle,
};

// ==========================================
// Component
// ==========================================

const Permissions: React.FC = () => {
    const { colorMode } = useColorMode();
    const [searchQuery, setSearchQuery] = useState("");
    const [categoryFilter, setCategoryFilter] = useState<string>("all");

    usePageTitle("Permissions Audit Dashboard");

    const bgMain = getColor("background.main", colorMode);
    const cardBg = useColorModeValue("white", "rgba(20, 20, 20, 0.9)");
    const cardBorder = useColorModeValue(getColor("border.light", "light"), "rgba(255, 255, 255, 0.1)");
    const textPrimary = useColorModeValue("#1A202C", "#FFFFFF");
    const textSecondary = useColorModeValue("#4A5568", "rgba(255, 255, 255, 0.7)");
    const inputBg = useColorModeValue("white", "gray.700");
    const tableBg = useColorModeValue("white", "rgba(30, 30, 35, 0.8)");
    const tableHeaderBg = useColorModeValue("gray.50", "rgba(40, 40, 45, 0.8)");

    // Hoisted color values (React hooks can't be called conditionally)
    const headerIconBg = useColorModeValue("blue.50", "rgba(66, 153, 225, 0.15)");
    const greenCardBg = useColorModeValue("green.50", "rgba(72, 187, 120, 0.08)");
    const greenCardBorder = useColorModeValue("green.200", "rgba(72, 187, 120, 0.2)");
    const strategyGoalBg = useColorModeValue("blue.50", "rgba(66, 153, 225, 0.1)");
    const blueStatBg = useColorModeValue("blue.50", "rgba(66, 153, 225, 0.15)");
    const purpleStatBg = useColorModeValue("purple.50", "rgba(128, 90, 213, 0.15)");
    const orangeStatBg = useColorModeValue("orange.50", "rgba(237, 137, 54, 0.15)");
    const greenStatBg = useColorModeValue("green.50", "rgba(72, 187, 120, 0.15)");
    const tealStatBg = useColorModeValue("teal.50", "rgba(56, 178, 172, 0.15)");
    const blueHelpBg = useColorModeValue("blue.50", "rgba(66, 153, 225, 0.08)");
    const blueHelpBorder = useColorModeValue("blue.100", "rgba(66, 153, 225, 0.2)");
    const purpleHelpBg = useColorModeValue("purple.50", "rgba(128, 90, 213, 0.08)");
    const purpleHelpBorder = useColorModeValue("purple.100", "rgba(128, 90, 213, 0.2)");
    const tealCardBg = useColorModeValue("teal.50", "rgba(56, 178, 172, 0.08)");
    const tealCardBorder = useColorModeValue("teal.100", "rgba(56, 178, 172, 0.2)");
    const tealInnerBg = useColorModeValue("teal.100", "rgba(56, 178, 172, 0.2)");
    const umbrellaCardBorder = useColorModeValue("teal.200", "rgba(255, 255, 255, 0.1)");
    const subtleBg = useColorModeValue("rgba(255,255,255,0.05)", "rgba(255, 255, 255, 0.05)");
    const purpleRowBg = useColorModeValue("purple.50", "rgba(128, 90, 213, 0.08)");
    const purpleRowBgAlt = useColorModeValue("purple.50", "rgba(128, 90, 213, 0.06)");
    const tealRowBg = useColorModeValue("teal.50", "rgba(56, 178, 172, 0.08)");
    const tealRowBgAlt = useColorModeValue("teal.50", "rgba(56, 178, 172, 0.06)");
    const yellowRowBg = useColorModeValue("yellow.50", "rgba(236, 201, 75, 0.08)");
    const blueRowBg = useColorModeValue("blue.50", "rgba(66, 153, 225, 0.08)");

    const { data, loading, error } = useQuery<PermissionsAuditData>(PERMISSIONS_AUDIT);

    // Filtered permissions
    const filteredPermissions = useMemo(() => {
        if (!data) return [];
        return data.permissionsAudit.permissions.filter(perm => {
            const matchesSearch = searchQuery === "" ||
                perm.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                perm.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                perm.usedInModules.some(m => m.moduleId.toLowerCase().includes(searchQuery.toLowerCase()));
            const matchesCategory = categoryFilter === "all" || perm.category === categoryFilter;
            return matchesSearch && matchesCategory;
        });
    }, [data, searchQuery, categoryFilter]);

    // Filtered modules
    const filteredModules = useMemo(() => {
        if (!data) return [];
        return data.permissionsAudit.modules.filter(mod =>
            searchQuery === "" ||
            mod.moduleId.toLowerCase().includes(searchQuery.toLowerCase()) ||
            mod.permissionsUsed.some(p => p.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    }, [data, searchQuery]);

    // Umbrella permissions (for the explorer tab)
    const umbrellaPermissions = useMemo(() => {
        if (!data) return [];
        return data.permissionsAudit.permissions.filter(p =>
            p.category === "umbrella" || p.category === "system"
        ).filter(p => p.moduleCount > 0).sort((a, b) => b.moduleCount - a.moduleCount);
    }, [data]);

    // Orphaned permissions
    const orphanedPermissions = useMemo(() => {
        if (!data) return [];
        return data.permissionsAudit.permissions.filter(p => p.isOrphan);
    }, [data]);

    if (loading) {
        return (
            <Box bg={bgMain} minH="100vh">
            
                <Container maxW="container.xl" py={8}>
                    <VStack spacing={4} py={20}>
                        <Spinner size="xl" color="blue.500" />
                        <Text color={textSecondary}>Loading permissions audit...</Text>
                    </VStack>
                </Container>
            </Box>
        );
    }

    if (error) {
        return (
            <Box bg={bgMain} minH="100vh">
                <Container maxW="container.xl" py={8}>
                    <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <AlertTitle>Failed to load permissions audit</AlertTitle>
                        <AlertDescription>{error.message}</AlertDescription>
                    </Alert>
                </Container>
            </Box>
        );
    }

    const audit = data!.permissionsAudit;

    return (
        <Box bg={bgMain} minH="100vh">
            <Container maxW="container.xl" py={8}>
                <VStack spacing={8} align="stretch">
                    {/* Header */}
                    <VStack spacing={3} align="start">
                        <HStack spacing={3}>
                            <Box
                                p={2}
                                borderRadius="lg"
                                bg={headerIconBg}
                            >
                                <Icon as={FiShield} boxSize={6} color="blue.400" />
                            </Box>
                            <VStack spacing={0} align="start">
                                <Heading color={textPrimary} size="lg">Permissions Audit Dashboard</Heading>
                                <Text color={textSecondary} fontSize="sm">
                                    Live view of all {audit.totalPermissions} permissions across {audit.totalModules} modules. Auto-updates when permissions.ts files change.
                                </Text>
                            </VStack>
                        </HStack>
                    </VStack>

                    {/* Single Source of Truth Note */}
                    <Card
                        bg={greenCardBg}
                        borderWidth="1px"
                        borderColor={greenCardBorder}
                    >
                        <CardBody py={3} px={5}>
                            <HStack spacing={3} align="start">
                                <Box mt={1}>
                                    <Icon as={FiCheckCircle} color="green.400" boxSize={5} />
                                </Box>
                                <VStack align="start" spacing={1}>
                                    <Text color={textPrimary} fontWeight="semibold" fontSize="sm">
                                        Single Source of Truth
                                    </Text>
                                    <Text color={textSecondary} fontSize="sm">
                                        All permission data on this page is computed live from each module's{" "}
                                        <strong>permissions.ts</strong> file in the backend
                                        (e.g. <code>business-builder-backend/src/modules/admissions/permissions.ts</code>).
                                        The <strong>ClientPermission</strong> enum in <code>Client.ts</code> defines
                                        all available permission strings, and each module's permissions.ts declares which
                                        permissions grant view/create/edit/delete access. Nothing on this page is hardcoded —
                                        change a permissions.ts file and this dashboard updates automatically.
                                    </Text>
                                </VStack>
                            </HStack>
                        </CardBody>
                    </Card>

                    {/* Cleanup Strategy */}
                    <Accordion allowToggle>
                        <AccordionItem border="none">
                            <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                                <AccordionButton px={6} py={4} _hover={{ bg: "transparent" }}>
                                    <HStack flex="1" spacing={3}>
                                        <Badge colorScheme="blue" fontSize="sm" px={2}>STRATEGY</Badge>
                                        <Text color={textPrimary} fontWeight="semibold" fontSize="md">
                                            Permissions Cleanup Roadmap
                                        </Text>
                                        <Badge colorScheme="gray" variant="outline" fontSize="xs">
                                            {audit.orphanCount} orphans to resolve
                                        </Badge>
                                    </HStack>
                                    <AccordionIcon color={textSecondary} />
                                </AccordionButton>
                                <AccordionPanel px={6} pb={6}>
                                    <VStack align="stretch" spacing={5}>
                                        {/* Phase 1 */}
                                        <Box>
                                            <HStack spacing={2} mb={2}>
                                                <Badge colorScheme="green" fontSize="xs">PHASE 1</Badge>
                                                <Text color={textPrimary} fontWeight="semibold" fontSize="sm">
                                                    Migrate orphaned permissions to permissions.ts
                                                </Text>
                                            </HStack>
                                            <Text color={textSecondary} fontSize="sm" pl={4} mb={2}>
                                                Check the Health Check tab for orphaned permissions. These are module-level permissions
                                                defined in the ClientPermission enum but not in any module's permissions.ts file.
                                                For each one:
                                            </Text>
                                            <VStack align="start" spacing={1} pl={8}>
                                                <Text color={textSecondary} fontSize="sm">
                                                    1. Find which resolver uses it (search for the permission name in resolvers/)
                                                </Text>
                                                <Text color={textSecondary} fontSize="sm">
                                                    2. If the module already has a permissions.ts, add the permission to the right level
                                                </Text>
                                                <Text color={textSecondary} fontSize="sm">
                                                    3. If not, create a permissions.ts (copy admissions template) and register in modulePermissions.resolver.ts
                                                </Text>
                                                <Text color={textSecondary} fontSize="sm">
                                                    4. Update the resolver to import from permissions.ts instead of using inline hasPermission()
                                                </Text>
                                            </VStack>
                                            <Text color={textSecondary} fontSize="xs" pl={4} mt={2} fontStyle="italic">
                                                Priority: REWARDS_* (4 perms), ACCOUNTABILITY_* (3 perms), DAY_BOOK/REFERRALS (5 perms) — these are entire module groups
                                            </Text>
                                        </Box>

                                        {/* Phase 2 */}
                                        <Box>
                                            <HStack spacing={2} mb={2}>
                                                <Badge colorScheme="blue" fontSize="xs">PHASE 2</Badge>
                                                <Text color={textPrimary} fontWeight="semibold" fontSize="sm">
                                                    Review umbrella role coverage
                                                </Text>
                                            </HStack>
                                            <Text color={textSecondary} fontSize="sm" pl={4}>
                                                Use the Umbrella Explorer tab to check what HEALTHCARE_ROLE and CATERING_ROLE
                                                actually grant. Ask: "Is this too broad? Should some modules have their own specific
                                                permissions instead of relying on the umbrella?" For example, should a nurse have
                                                delete access on all clinical modules, or should that be restricted?
                                            </Text>
                                        </Box>

                                        {/* Phase 3 */}
                                        <Box>
                                            <HStack spacing={2} mb={2}>
                                                <Badge colorScheme="purple" fontSize="xs">PHASE 3</Badge>
                                                <Text color={textPrimary} fontWeight="semibold" fontSize="sm">
                                                    Remove truly unused permissions
                                                </Text>
                                            </HStack>
                                            <Text color={textSecondary} fontSize="sm" pl={4}>
                                                After Phase 1, any remaining orphans are genuinely unused. Search the entire codebase
                                                (backend + frontend) for each one. If it's not referenced anywhere, remove it from the
                                                ClientPermission enum in Client.ts. Candidates: VIDEO_SERVER_* (module was deleted),
                                                IPFS_* (if not used).
                                            </Text>
                                        </Box>

                                        {/* Phase 4 */}
                                        <Box>
                                            <HStack spacing={2} mb={2}>
                                                <Badge colorScheme="orange" fontSize="xs">PHASE 4</Badge>
                                                <Text color={textPrimary} fontWeight="semibold" fontSize="sm">
                                                    Before creating new permissions — check here first
                                                </Text>
                                            </HStack>
                                            <Text color={textSecondary} fontSize="sm" pl={4}>
                                                Use the By Permission tab and search before adding any new permission.
                                                Check if an existing permission already covers what you need. For example,
                                                "view hours worked" might already be covered by EMPLOYEE_VIEWER or EMPLOYEE_HR.
                                                Only create a new permission if no existing one fits.
                                            </Text>
                                        </Box>

                                        {/* Goal */}
                                        <Box
                                            p={3}
                                            borderRadius="md"
                                            borderWidth="1px"
                                            borderColor={cardBorder}
                                            bg={strategyGoalBg}
                                        >
                                            <Text color={textPrimary} fontSize="sm" fontWeight="semibold">
                                                Goal: Zero orphaned permissions, every permission traceable through permissions.ts,
                                                umbrella roles reviewed and intentional.
                                            </Text>
                                        </Box>
                                    </VStack>
                                </AccordionPanel>
                            </Card>
                        </AccordionItem>
                    </Accordion>

                    {/* Stats */}
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                        <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                            <CardBody>
                                <HStack spacing={3}>
                                    <Box p={2} borderRadius="md" bg={blueStatBg}>
                                        <Icon as={FiKey} boxSize={5} color="blue.400" />
                                    </Box>
                                    <Stat>
                                        <StatLabel color={textSecondary}>Total Permissions</StatLabel>
                                        <StatNumber color={textPrimary} fontSize="2xl">{audit.totalPermissions}</StatNumber>
                                    </Stat>
                                </HStack>
                            </CardBody>
                        </Card>
                        <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                            <CardBody>
                                <HStack spacing={3}>
                                    <Box p={2} borderRadius="md" bg={purpleStatBg}>
                                        <Icon as={FiBox} boxSize={5} color="purple.400" />
                                    </Box>
                                    <Stat>
                                        <StatLabel color={textSecondary}>Total Modules</StatLabel>
                                        <StatNumber color={textPrimary} fontSize="2xl">{audit.totalModules}</StatNumber>
                                    </Stat>
                                </HStack>
                            </CardBody>
                        </Card>
                        <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                            <CardBody>
                                <HStack spacing={3}>
                                    <Box p={2} borderRadius="md" bg={audit.orphanCount > 0 ? orangeStatBg : greenStatBg}>
                                        <Icon
                                            as={audit.orphanCount > 0 ? FiAlertTriangle : FiCheckCircle}
                                            boxSize={5}
                                            color={audit.orphanCount > 0 ? "orange.400" : "green.400"}
                                        />
                                    </Box>
                                    <Stat>
                                        <StatLabel color={textSecondary}>Needs Migration</StatLabel>
                                        <StatNumber color={audit.orphanCount > 0 ? "orange.400" : "green.400"} fontSize="2xl">
                                            {audit.orphanCount}
                                        </StatNumber>
                                    </Stat>
                                </HStack>
                            </CardBody>
                        </Card>
                        <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                            <CardBody>
                                <HStack spacing={3}>
                                    <Box p={2} borderRadius="md" bg={tealStatBg}>
                                        <Icon as={FiLayers} boxSize={5} color="teal.400" />
                                    </Box>
                                    <Stat>
                                        <StatLabel color={textSecondary}>Categories</StatLabel>
                                        <StatNumber color={textPrimary} fontSize="2xl">5</StatNumber>
                                    </Stat>
                                </HStack>
                            </CardBody>
                        </Card>
                    </SimpleGrid>

                    {/* Color Legend */}
                    <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                        <CardBody py={3}>
                            <HStack spacing={6} flexWrap="wrap" justify="center">
                                <HStack spacing={2}>
                                    <Icon as={FiEye} color="green.400" boxSize={3} />
                                    <Badge colorScheme="green" fontSize="2xs">view</Badge>
                                </HStack>
                                <HStack spacing={2}>
                                    <Icon as={FiPlusCircle} color="blue.400" boxSize={3} />
                                    <Badge colorScheme="blue" fontSize="2xs">create</Badge>
                                </HStack>
                                <HStack spacing={2}>
                                    <Icon as={FiEdit} color="orange.400" boxSize={3} />
                                    <Badge colorScheme="orange" fontSize="2xs">edit</Badge>
                                </HStack>
                                <HStack spacing={2}>
                                    <Icon as={FiTrash2} color="red.400" boxSize={3} />
                                    <Badge colorScheme="red" fontSize="2xs">delete</Badge>
                                </HStack>
                                <Text color={textSecondary} fontSize="2xs">|</Text>
                                <HStack spacing={2}>
                                    <Icon as={FiShield} color="purple.400" boxSize={3} />
                                    <Badge colorScheme="purple" fontSize="2xs" variant="outline">System</Badge>
                                </HStack>
                                <HStack spacing={2}>
                                    <Icon as={FiUmbrella} color="teal.400" boxSize={3} />
                                    <Badge colorScheme="teal" fontSize="2xs" variant="outline">Umbrella</Badge>
                                </HStack>
                                <HStack spacing={2}>
                                    <Icon as={FiBox} color="blue.400" boxSize={3} />
                                    <Badge colorScheme="blue" fontSize="2xs" variant="outline">Module</Badge>
                                </HStack>
                                <HStack spacing={2}>
                                    <Icon as={FiEye} color="yellow.500" boxSize={3} />
                                    <Badge colorScheme="yellow" fontSize="2xs" variant="outline">Field</Badge>
                                </HStack>
                                <HStack spacing={2}>
                                    <Icon as={FiUsers} color="gray.400" boxSize={3} />
                                    <Badge colorScheme="gray" fontSize="2xs" variant="outline">Role</Badge>
                                </HStack>
                            </HStack>
                        </CardBody>
                    </Card>

                    {/* Search and Filters */}
                    <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                        <CardBody>
                            <HStack spacing={4}>
                                <InputGroup flex={1}>
                                    <InputLeftElement pointerEvents="none">
                                        <SearchIcon color={textSecondary} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search permissions, modules, or descriptions..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        bg={inputBg}
                                        color={textPrimary}
                                    />
                                </InputGroup>
                                <Select
                                    w="200px"
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                    bg={inputBg}
                                    color={textPrimary}
                                >
                                    <option value="all">All Categories</option>
                                    <option value="system">System</option>
                                    <option value="umbrella">Umbrella Role</option>
                                    <option value="module">Module</option>
                                    <option value="field-level">Field-Level</option>
                                    <option value="role">Business Role</option>
                                </Select>
                            </HStack>
                        </CardBody>
                    </Card>

                    {/* Tabs */}
                    <Tabs colorScheme="blue">
                        <TabList>
                            <Tab color={textPrimary}>
                                <HStack spacing={2}>
                                    <Icon as={FiGrid} boxSize={4} />
                                    <Text>By Module</Text>
                                </HStack>
                            </Tab>
                            <Tab color={textPrimary}>
                                <HStack spacing={2}>
                                    <Icon as={FiList} boxSize={4} />
                                    <Text>By Permission</Text>
                                </HStack>
                            </Tab>
                            <Tab color={textPrimary}>
                                <HStack spacing={2}>
                                    <Icon as={FiUmbrella} boxSize={4} />
                                    <Text>Umbrella Explorer</Text>
                                </HStack>
                            </Tab>
                            <Tab color={textPrimary}>
                                <HStack spacing={2}>
                                    <Icon as={FiActivity} boxSize={4} />
                                    <Text>Health Check</Text>
                                    {audit.orphanCount > 0 && (
                                        <Badge colorScheme="orange" fontSize="xs">{audit.orphanCount}</Badge>
                                    )}
                                </HStack>
                            </Tab>
                        </TabList>

                        <TabPanels>
                            {/* =============================== */}
                            {/* Tab 1: By Module                */}
                            {/* =============================== */}
                            <TabPanel px={0}>
                                <VStack spacing={4} align="stretch">
                                <Card bg={blueHelpBg} borderWidth="1px" borderColor={blueHelpBorder} mb={0}>
                                    <CardBody py={3} px={4}>
                                        <HStack spacing={3}>
                                            <Icon as={FiMap} color="blue.400" boxSize={4} />
                                            <Text color={textSecondary} fontSize="sm">
                                                <strong>What module needs what permission?</strong> Click any module to expand and see exactly which permissions are required for each access level (view, create, edit, delete).
                                            </Text>
                                        </HStack>
                                    </CardBody>
                                </Card>
                                <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                                    <CardBody p={0}>
                                        <Accordion allowMultiple>
                                            {filteredModules.map((mod) => (
                                                <AccordionItem key={mod.moduleId} border="none">
                                                    <AccordionButton
                                                        _hover={{ bg: tableHeaderBg }}
                                                        py={3}
                                                        px={4}
                                                    >
                                                        <Box flex="1" textAlign="left">
                                                            <HStack spacing={3}>
                                                                <Text
                                                                    color={textPrimary}
                                                                    fontWeight="semibold"
                                                                    fontSize="sm"
                                                                >
                                                                    {mod.moduleId}
                                                                </Text>
                                                                <Wrap spacing={1}>
                                                                    {mod.levels.map(level => (
                                                                        <WrapItem key={level}>
                                                                            <Badge
                                                                                colorScheme={LEVEL_COLORS[level] || "gray"}
                                                                                fontSize="2xs"
                                                                                variant="subtle"
                                                                            >
                                                                                {level}
                                                                            </Badge>
                                                                        </WrapItem>
                                                                    ))}
                                                                </Wrap>
                                                                <Badge
                                                                    colorScheme="gray"
                                                                    fontSize="2xs"
                                                                    variant="outline"
                                                                >
                                                                    {mod.permissionsUsed.length} perms
                                                                </Badge>
                                                            </HStack>
                                                        </Box>
                                                        <AccordionIcon color={textSecondary} />
                                                    </AccordionButton>
                                                    <AccordionPanel pb={4} px={4}>
                                                        <VStack align="stretch" spacing={2}>
                                                            {mod.levels.map(level => {
                                                                // Find permissions for this level from the audit data
                                                                const permsForLevel = audit.permissions
                                                                    .filter(p =>
                                                                        p.usedInModules.some(m =>
                                                                            m.moduleId === mod.moduleId &&
                                                                            m.levels.includes(level)
                                                                        )
                                                                    )
                                                                    .map(p => p.name);

                                                                return (
                                                                    <HStack key={level} align="start" spacing={3}>
                                                                        <Badge
                                                                            colorScheme={LEVEL_COLORS[level] || "gray"}
                                                                            fontSize="xs"
                                                                            minW="60px"
                                                                            textAlign="center"
                                                                        >
                                                                            {level}
                                                                        </Badge>
                                                                        <Wrap spacing={1}>
                                                                            {permsForLevel.map(perm => {
                                                                                const permData = audit.permissions.find(p => p.name === perm);
                                                                                return (
                                                                                    <WrapItem key={perm}>
                                                                                        <Tooltip
                                                                                            label={permData?.description || ""}
                                                                                            placement="top"
                                                                                        >
                                                                                            <Badge
                                                                                                colorScheme={
                                                                                                    CATEGORY_COLORS[permData?.category || "module"]
                                                                                                }
                                                                                                fontSize="xs"
                                                                                                cursor="help"
                                                                                            >
                                                                                                {perm}
                                                                                            </Badge>
                                                                                        </Tooltip>
                                                                                    </WrapItem>
                                                                                );
                                                                            })}
                                                                        </Wrap>
                                                                    </HStack>
                                                                );
                                                            })}
                                                        </VStack>
                                                    </AccordionPanel>
                                                </AccordionItem>
                                            ))}
                                        </Accordion>
                                    </CardBody>
                                </Card>
                                </VStack>
                            </TabPanel>

                            {/* =============================== */}
                            {/* Tab 2: By Permission            */}
                            {/* =============================== */}
                            <TabPanel px={0}>
                                <VStack spacing={4} align="stretch">
                                <Card bg={purpleHelpBg} borderWidth="1px" borderColor={purpleHelpBorder} mb={0}>
                                    <CardBody py={3} px={4}>
                                        <HStack spacing={3}>
                                            <Icon as={FiTarget} color="purple.400" boxSize={4} />
                                            <Text color={textSecondary} fontSize="sm">
                                                <strong>Does a permission already exist for what I need?</strong> Search below before creating a new one. Check the "Modules" column to see how widely each permission is used.
                                            </Text>
                                        </HStack>
                                    </CardBody>
                                </Card>
                                <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                                    <CardBody p={0}>
                                        <Box overflowX="auto">
                                            <Table variant="simple" bg={tableBg} size="sm">
                                                <Thead bg={tableHeaderBg}>
                                                    <Tr>
                                                        <Th color={textPrimary} borderColor={cardBorder}>Permission</Th>
                                                        <Th color={textPrimary} borderColor={cardBorder}>Category</Th>
                                                        <Th color={textPrimary} borderColor={cardBorder}>Description</Th>
                                                        <Th color={textPrimary} borderColor={cardBorder} isNumeric>Modules</Th>
                                                        <Th color={textPrimary} borderColor={cardBorder}>Used In</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {filteredPermissions.map((perm) => (
                                                        <Tr key={perm.name} opacity={perm.isOrphan ? 0.5 : 1}>
                                                            <Td borderColor={cardBorder}>
                                                                <HStack spacing={2}>
                                                                    <Badge
                                                                        colorScheme={CATEGORY_COLORS[perm.category]}
                                                                        fontSize="xs"
                                                                    >
                                                                        {perm.name}
                                                                    </Badge>
                                                                    {perm.isOrphan && (
                                                                        <Tooltip label="Orphaned - defined but not used in any module">
                                                                            <span>
                                                                                <Icon as={WarningIcon} color="orange.400" boxSize={3} />
                                                                            </span>
                                                                        </Tooltip>
                                                                    )}
                                                                </HStack>
                                                            </Td>
                                                            <Td borderColor={cardBorder}>
                                                                <Badge
                                                                    colorScheme={CATEGORY_COLORS[perm.category]}
                                                                    variant="outline"
                                                                    fontSize="2xs"
                                                                >
                                                                    {getCategoryLabel(perm.category)}
                                                                </Badge>
                                                            </Td>
                                                            <Td
                                                                color={textSecondary}
                                                                fontSize="xs"
                                                                borderColor={cardBorder}
                                                                maxW="300px"
                                                            >
                                                                {perm.description}
                                                            </Td>
                                                            <Td
                                                                borderColor={cardBorder}
                                                                isNumeric
                                                                color={textPrimary}
                                                                fontWeight="bold"
                                                            >
                                                                {perm.moduleCount}
                                                            </Td>
                                                            <Td borderColor={cardBorder}>
                                                                <Wrap spacing={1}>
                                                                    {perm.usedInModules.slice(0, 5).map(m => (
                                                                        <WrapItem key={m.moduleId}>
                                                                            <Tooltip
                                                                                label={`Levels: ${m.levels.join(", ")}`}
                                                                                placement="top"
                                                                            >
                                                                                <Badge
                                                                                    fontSize="2xs"
                                                                                    colorScheme="gray"
                                                                                    cursor="help"
                                                                                >
                                                                                    {m.moduleId}
                                                                                </Badge>
                                                                            </Tooltip>
                                                                        </WrapItem>
                                                                    ))}
                                                                    {perm.usedInModules.length > 5 && (
                                                                        <WrapItem>
                                                                            <Badge fontSize="2xs" colorScheme="gray" variant="outline">
                                                                                +{perm.usedInModules.length - 5} more
                                                                            </Badge>
                                                                        </WrapItem>
                                                                    )}
                                                                </Wrap>
                                                            </Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </Box>
                                    </CardBody>
                                </Card>
                                </VStack>
                            </TabPanel>

                            {/* =============================== */}
                            {/* Tab 3: Umbrella Explorer         */}
                            {/* =============================== */}
                            <TabPanel px={0}>
                                <VStack spacing={4} align="stretch">
                                    <Card bg={tealCardBg} borderWidth="1px" borderColor={tealCardBorder}>
                                        <CardBody>
                                            <HStack spacing={3} align="start">
                                                <Box p={2} borderRadius="md" bg={tealInnerBg}>
                                                    <Icon as={FiUmbrella} boxSize={5} color="teal.500" />
                                                </Box>
                                                <VStack align="start" spacing={1}>
                                                    <Text color={textPrimary} fontWeight="semibold" fontSize="md">
                                                        Umbrella Role Explorer
                                                    </Text>
                                                    <Text color={textSecondary} fontSize="sm">
                                                        <strong>"If I give someone HEALTHCARE_ROLE, what can they do?"</strong> Each card below shows every module
                                                        and access level that a single permission grants. Use this to understand the blast radius before
                                                        assigning umbrella permissions to users.
                                                    </Text>
                                                </VStack>
                                            </HStack>
                                        </CardBody>
                                    </Card>

                                    {umbrellaPermissions.map(perm => (
                                        <Card
                                            key={perm.name}
                                            bg={cardBg}
                                            borderWidth="2px"
                                            borderColor={umbrellaCardBorder}
                                        >
                                            <CardBody>
                                                <VStack align="stretch" spacing={4}>
                                                    <HStack justify="space-between">
                                                        <HStack spacing={3}>
                                                            <Box
                                                                p={2}
                                                                borderRadius="md"
                                                                bg={subtleBg}
                                                            >
                                                                <Icon
                                                                    as={CATEGORY_ICONS[perm.category] || FiKey}
                                                                    boxSize={5}
                                                                    color={`${CATEGORY_COLORS[perm.category]}.400`}
                                                                />
                                                            </Box>
                                                            <Badge
                                                                colorScheme={CATEGORY_COLORS[perm.category]}
                                                                fontSize="md"
                                                                px={3}
                                                                py={1}
                                                            >
                                                                {perm.name}
                                                            </Badge>
                                                            <Badge
                                                                colorScheme={CATEGORY_COLORS[perm.category]}
                                                                variant="outline"
                                                                fontSize="xs"
                                                            >
                                                                {getCategoryLabel(perm.category)}
                                                            </Badge>
                                                        </HStack>
                                                        <HStack spacing={2}>
                                                            <Icon as={FiBox} color="blue.400" boxSize={4} />
                                                            <Badge
                                                                colorScheme="blue"
                                                                variant="outline"
                                                                fontSize="sm"
                                                            >
                                                                {perm.moduleCount} modules
                                                            </Badge>
                                                        </HStack>
                                                    </HStack>
                                                    <Text color={textSecondary} fontSize="sm">
                                                        {perm.description}
                                                    </Text>

                                                    <Box overflowX="auto">
                                                        <Table size="sm" variant="simple">
                                                            <Thead bg={tableHeaderBg}>
                                                                <Tr>
                                                                    <Th color={textPrimary} borderColor={cardBorder}>
                                                                        Module
                                                                    </Th>
                                                                    <Th color={textPrimary} borderColor={cardBorder}>
                                                                        Access Levels Granted
                                                                    </Th>
                                                                </Tr>
                                                            </Thead>
                                                            <Tbody>
                                                                {perm.usedInModules
                                                                    .slice()
                                                                    .sort((a, b) => a.moduleId.localeCompare(b.moduleId))
                                                                    .map(m => (
                                                                        <Tr key={m.moduleId}>
                                                                            <Td
                                                                                color={textPrimary}
                                                                                fontWeight="medium"
                                                                                fontSize="sm"
                                                                                borderColor={cardBorder}
                                                                            >
                                                                                {m.moduleId}
                                                                            </Td>
                                                                            <Td borderColor={cardBorder}>
                                                                                <Wrap spacing={1}>
                                                                                    {m.levels.map(level => (
                                                                                        <WrapItem key={level}>
                                                                                            <Badge
                                                                                                colorScheme={LEVEL_COLORS[level] || "gray"}
                                                                                                fontSize="xs"
                                                                                            >
                                                                                                <HStack spacing={1}>
                                                                                                    {LEVEL_ICONS[level] && (
                                                                                                        <Icon as={LEVEL_ICONS[level]} boxSize={3} />
                                                                                                    )}
                                                                                                    <Text>{level}</Text>
                                                                                                </HStack>
                                                                                            </Badge>
                                                                                        </WrapItem>
                                                                                    ))}
                                                                                </Wrap>
                                                                            </Td>
                                                                        </Tr>
                                                                    ))}
                                                            </Tbody>
                                                        </Table>
                                                    </Box>
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    ))}
                                </VStack>
                            </TabPanel>

                            {/* =============================== */}
                            {/* Tab 4: Health Check              */}
                            {/* =============================== */}
                            <TabPanel px={0}>
                                <VStack spacing={4} align="stretch">
                                    {/* Orphaned Permissions */}
                                    <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                                        <CardBody>
                                            <VStack align="stretch" spacing={4}>
                                                <HStack>
                                                    <Icon
                                                        as={orphanedPermissions.length > 0 ? WarningIcon : CheckCircleIcon}
                                                        color={orphanedPermissions.length > 0 ? "orange.400" : "green.400"}
                                                    />
                                                    <Heading size="sm" color={textPrimary}>
                                                        Orphaned Permissions ({orphanedPermissions.length})
                                                    </Heading>
                                                </HStack>
                                                <Text color={textSecondary} fontSize="sm">
                                                    Defined in ClientPermission enum but not used in any module's permissions.ts.
                                                    These may be unused, implemented inline in resolvers, or reserved for future use.
                                                </Text>
                                                {orphanedPermissions.length > 0 ? (
                                                    <Box overflowX="auto">
                                                        <Table size="sm" variant="simple">
                                                            <Thead bg={tableHeaderBg}>
                                                                <Tr>
                                                                    <Th color={textPrimary} borderColor={cardBorder}>Permission</Th>
                                                                    <Th color={textPrimary} borderColor={cardBorder}>Category</Th>
                                                                    <Th color={textPrimary} borderColor={cardBorder}>Description</Th>
                                                                </Tr>
                                                            </Thead>
                                                            <Tbody>
                                                                {orphanedPermissions.map(perm => (
                                                                    <Tr key={perm.name}>
                                                                        <Td borderColor={cardBorder}>
                                                                            <Badge
                                                                                colorScheme="orange"
                                                                                fontSize="xs"
                                                                            >
                                                                                {perm.name}
                                                                            </Badge>
                                                                        </Td>
                                                                        <Td borderColor={cardBorder}>
                                                                            <Badge
                                                                                colorScheme={CATEGORY_COLORS[perm.category]}
                                                                                variant="outline"
                                                                                fontSize="2xs"
                                                                            >
                                                                                {getCategoryLabel(perm.category)}
                                                                            </Badge>
                                                                        </Td>
                                                                        <Td
                                                                            color={textSecondary}
                                                                            fontSize="xs"
                                                                            borderColor={cardBorder}
                                                                        >
                                                                            {perm.description}
                                                                        </Td>
                                                                    </Tr>
                                                                ))}
                                                            </Tbody>
                                                        </Table>
                                                    </Box>
                                                ) : (
                                                    <Alert status="success" borderRadius="md">
                                                        <AlertIcon />
                                                        <AlertTitle fontSize="sm">All clean!</AlertTitle>
                                                        <AlertDescription fontSize="sm">
                                                            Every permission in the enum is used in at least one module.
                                                        </AlertDescription>
                                                    </Alert>
                                                )}
                                            </VStack>
                                        </CardBody>
                                    </Card>

                                    {/* Permission Category Breakdown */}
                                    <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                                        <CardBody>
                                            <VStack align="stretch" spacing={4}>
                                                <HStack>
                                                    <Icon as={InfoIcon} color="blue.400" />
                                                    <Heading size="sm" color={textPrimary}>
                                                        Permission Categories
                                                    </Heading>
                                                </HStack>
                                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                                    {["system", "umbrella", "module", "field-level", "role"].map(cat => {
                                                        const count = audit.permissions.filter(p => p.category === cat).length;
                                                        const CatIcon = CATEGORY_ICONS[cat] || FiKey;
                                                        return (
                                                            <HStack
                                                                key={cat}
                                                                p={3}
                                                                borderWidth="1px"
                                                                borderColor={cardBorder}
                                                                borderRadius="md"
                                                                spacing={3}
                                                            >
                                                                <Box
                                                                    p={2}
                                                                    borderRadius="md"
                                                                    bg={subtleBg}
                                                                >
                                                                    <Icon
                                                                        as={CatIcon}
                                                                        boxSize={4}
                                                                        color={`${CATEGORY_COLORS[cat]}.400`}
                                                                    />
                                                                </Box>
                                                                <VStack align="start" spacing={0} flex={1}>
                                                                    <HStack justify="space-between" w="100%">
                                                                        <Badge
                                                                            colorScheme={CATEGORY_COLORS[cat]}
                                                                            fontSize="xs"
                                                                        >
                                                                            {getCategoryLabel(cat)}
                                                                        </Badge>
                                                                        <Text
                                                                            color={textPrimary}
                                                                            fontWeight="bold"
                                                                            fontSize="sm"
                                                                        >
                                                                            {count}
                                                                        </Text>
                                                                    </HStack>
                                                                    <Text
                                                                        color={textSecondary}
                                                                        fontSize="2xs"
                                                                        mt={1}
                                                                    >
                                                                        {CATEGORY_DESCRIPTIONS[cat]}
                                                                    </Text>
                                                                </VStack>
                                                            </HStack>
                                                        );
                                                    })}
                                                </SimpleGrid>
                                            </VStack>
                                        </CardBody>
                                    </Card>

                                    {/* Permission Hierarchy Guide */}
                                    <Card bg={cardBg} borderWidth="1px" borderColor={cardBorder}>
                                        <CardBody>
                                            <VStack align="start" spacing={4}>
                                                <HStack>
                                                    <Icon as={FiLayers} color="blue.400" />
                                                    <Heading size="sm" color={textPrimary}>Permission Hierarchy</Heading>
                                                </HStack>
                                                <Text color={textSecondary} fontSize="sm">
                                                    How permissions are checked, from broadest to most specific. Higher-level permissions
                                                    bypass lower-level checks.
                                                </Text>
                                                <VStack align="start" spacing={3} pl={2} w="100%">
                                                    <HStack
                                                        p={2}
                                                        borderRadius="md"
                                                        bg={purpleRowBg}
                                                        w="100%"
                                                    >
                                                        <Icon as={FiShield} color="purple.400" boxSize={4} />
                                                        <Badge colorScheme="purple">MASTER_ADMIN</Badge>
                                                        <Text color={textSecondary} fontSize="sm">
                                                            Bypasses ALL checks at middleware level. Never appears in permissions.ts.
                                                        </Text>
                                                    </HStack>
                                                    <HStack
                                                        p={2}
                                                        borderRadius="md"
                                                        bg={purpleRowBgAlt}
                                                        w="100%"
                                                    >
                                                        <Icon as={FiShield} color="purple.300" boxSize={4} />
                                                        <Badge colorScheme="purple">ADMIN</Badge>
                                                        <Text color={textSecondary} fontSize="sm">
                                                            Included in every module's permission arrays. Can do everything in every module.
                                                        </Text>
                                                    </HStack>
                                                    <HStack
                                                        p={2}
                                                        borderRadius="md"
                                                        bg={tealRowBg}
                                                        w="100%"
                                                    >
                                                        <Icon as={FiUmbrella} color="teal.400" boxSize={4} />
                                                        <Badge colorScheme="teal">HEALTHCARE_ROLE</Badge>
                                                        <Text color={textSecondary} fontSize="sm">
                                                            Umbrella: grants view/create/edit across all clinical healthcare modules
                                                        </Text>
                                                    </HStack>
                                                    <HStack
                                                        p={2}
                                                        borderRadius="md"
                                                        bg={tealRowBgAlt}
                                                        w="100%"
                                                    >
                                                        <Icon as={FiUmbrella} color="teal.300" boxSize={4} />
                                                        <Badge colorScheme="teal">CATERING_ROLE</Badge>
                                                        <Text color={textSecondary} fontSize="sm">
                                                            Umbrella: grants access to meal planning modules
                                                        </Text>
                                                    </HStack>
                                                    <HStack
                                                        p={2}
                                                        borderRadius="md"
                                                        bg={yellowRowBg}
                                                        w="100%"
                                                    >
                                                        <Icon as={FiEye} color="yellow.500" boxSize={4} />
                                                        <Badge colorScheme="yellow">EMPLOYEE_HR</Badge>
                                                        <Text color={textSecondary} fontSize="sm">
                                                            Field-level: shows phone/email on employee records. Does not grant module access.
                                                        </Text>
                                                    </HStack>
                                                    <HStack
                                                        p={2}
                                                        borderRadius="md"
                                                        bg={blueRowBg}
                                                        w="100%"
                                                    >
                                                        <Icon as={FiBox} color="blue.400" boxSize={4} />
                                                        <Badge colorScheme="blue">MODULE_*</Badge>
                                                        <Text color={textSecondary} fontSize="sm">
                                                            Module-specific: VIEW / EDIT / ADMIN per module. The standard pattern.
                                                        </Text>
                                                    </HStack>
                                                </VStack>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                </VStack>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </VStack>
            </Container>

       
        </Box>
    );
};

export default Permissions;
